import React, { useEffect, useState } from 'react'
import className from 'classnames/bind'
import { useHistory } from 'react-router'
import InfiniteScroll from 'react-infinite-scroller'

import style from './MyhouseReviewListPage.module.scss'

import BlindContent from '@Components/atoms/BlindContent'
import MyhouseReviewHeader from '@Components/atoms/MyhouseReviewHeader'
import MyhouseReviewInfo from '@Components/atoms/MyhouseReviewInfo'
import MyhouseReviewList from '@Components/modules/MyhouseReviewList'
import useMyhouse from '@Hooks/myhouse/useMyhouse'
import useReview from '@Hooks/myhouse/review/useReview'
import useBoard from '@Hooks/myhouse/board/useBoard'
import { LAND_SERVICE_PREFIX_URL } from '@Root/contants/urlConstants'
import useModal from '@Hooks/myhouse/useModal'
import { MODAL_TYPE } from '@Root/contants/myhouseModal'
import { FETCH_STATUS } from '@Root/contants'
import { MY_HOUSE_AUTH_STATUS } from '@Root/contants/myhouse'
import MyhouseCommonProgress from '@Components/atoms/MyhouseCommonProgress'
import { NCLICK, NDS_PROTOCOL } from '@Root/contants/lcs'
import { handleNclick } from '@Helpers/nclick'
import { lcsSend } from '@Helpers/lcs'
import { useSessionStorage } from '@Root/utils/storageUtils'

const cx = className.bind(style)

const MyhouseReviewListPage: React.FC = () => {
  //변수선언부
  const { myhouse } = useMyhouse()
  const { review, loadReviewList, loadInitialReviewList, initializeReviewList } = useReview()
  const { getAuthStatusForBoard, getNoticesReadableComplexNo } = useBoard()
  const history = useHistory()
  const { open } = useModal()

  const { complexNo, authComplexNoMap } = myhouse
  const { totalCount, hasMore, list, listFetchStatus, complexName } = review
  const [isShowLoading, setIsShowLoading] = useState(true)
  const { getStorage } = useSessionStorage()

  const backMyhouseNo = getStorage(['bbs', 'complex', 'house', complexNo]) || authComplexNoMap?.[complexNo]?.[0]?.houseNo || ''

  const headerProps = {
    prev: {
      href: backMyhouseNo ? `${LAND_SERVICE_PREFIX_URL.MYHOUSE}/${backMyhouseNo}` : `${process.env.REACT_APP_SPACE_WEB_URL}/complex/info/${complexNo}`,
      text: '뒤로가기',
    },
    title: (!getNoticesReadableComplexNo(complexNo) && complexName) || '입주민 리뷰',
    enrollment: {
      onClick: (e: React.SyntheticEvent) => {
        e.preventDefault()
        handleNclick(NCLICK.MY_HOUSE.REVIEW_LIST.ENROL, e)

        if (getAuthStatusForBoard() === MY_HOUSE_AUTH_STATUS.COMPLETE) {
          history.push(`${LAND_SERVICE_PREFIX_URL.MYHOUSE_COMPLEXES}/${complexNo}/board/form/reviews`)
          return
        } else if (getAuthStatusForBoard() === MY_HOUSE_AUTH_STATUS.PROGRESS) {
          open(MODAL_TYPE.AUTH_NOT_COMPLETED)
        } else {
          open(MODAL_TYPE.MYHOUSE_AUTHENTICATION_REQUIRED, { complexNo: complexNo })
        }
      },
      text: '등록하기',
      disabled: !getNoticesReadableComplexNo(complexNo),
    },
  }

  //함수정의부
  const scrollHandler = () => {
    if (complexNo && listFetchStatus === FETCH_STATUS.SUCCESS) {
      loadReviewList({ complexNo, offset: list.length, limit: 20 })
    }
  }

  //useEffect
  useEffect(() => {
    lcsSend(NDS_PROTOCOL.MY_HOUSE.REVIEW.LIST)
    return () => {
      initializeReviewList()
    }
  }, [])

  useEffect(() => {
    if (complexNo) {
      loadInitialReviewList({ complexNo, offset: 0, limit: 20 })
    }
  }, [complexNo])

  useEffect(() => {
    if (listFetchStatus === FETCH_STATUS.SUCCESS) {
      setIsShowLoading(false)
      if (!list?.length) {
        if (complexNo) {
          if (authComplexNoMap[complexNo] && authComplexNoMap[complexNo].length > 0 && authComplexNoMap[complexNo][0].houseNo) {
            history.replace(`/myhouse/${authComplexNoMap[complexNo][0].houseNo}`)
          } else {
            history.replace(LAND_SERVICE_PREFIX_URL.MYHOUSE)
          }
        } else {
          history.replace(LAND_SERVICE_PREFIX_URL.MYHOUSE)
        }
      }
    }
  }, [listFetchStatus])

  if (isShowLoading) {
    return <MyhouseCommonProgress isViewportHeight />
  }

  return (
    <>
      <MyhouseReviewHeader {...headerProps} />
      <section id="content" className={cx('content_myhouse', 'type_review')} role="main">
        <BlindContent />
        <div className={cx('content_review_inner')}>
          <MyhouseReviewInfo reviewCount={totalCount} />
          <InfiniteScroll loadMore={scrollHandler} hasMore={hasMore} loader={<div className="loader" key={0}></div>} threshold={100}>
            <MyhouseReviewList {...{ list }} />
          </InfiniteScroll>
        </div>
      </section>
    </>
  )
}

export default MyhouseReviewListPage
